/**
 * ==========================================================================
 * CHARM LAB - UPDATED MODULE LOADER WITH v2.1 DEVICE-SPECIFIC SCALING
 * Fixed to load the NEW scaling system with device-specific logic
 * Mobile: Base same size, charms scale by multiplier
 * Desktop: Base scales down, charms consistent
 * ==========================================================================
 */

class CharmLabLoader {
  constructor() {
    // Updated loading order with proper scaling system
    this.modules = [
      'charm-lab-utils.js',               // 1. Utilities
      'charm-lab-elements.js',            // 2. DOM elements
      'charm-lab-state.js',               // 3. State management
      'charm-lab-scaling-system.js',      // 4. NEW: Device-specific scaling system
      'charm-lab-events.js',              // 5. Event handling
      'charm-lab-base-jewelry.js',        // 6. Base jewelry module
      'charm-lab-selections.js',          // 7. Selections module
      'charm-lab-charms.js',              // 8. Charms module
      'charm-lab-cart.js',                // 9. Cart module
      'charm-lab-ui.js',                  // 10. UI updates
      'charm-lab-api.js',                 // 11. Public API
      'charm-lab-main.js'                 // 12. Main orchestrator
    ];
    
    this.loadedModules = new Set();
    this.baseUrl = this.getBaseUrl();
    this.isLoading = false;
    this.loadingPromise = null;
  }

  getBaseUrl() {
    // First try to find the loader script specifically
    const loaderScript = document.querySelector('script[src*="charm-lab-loader"]');
    if (loaderScript && loaderScript.src) {
      const src = loaderScript.src;
      const baseUrl = src.replace(/\/[^\/]*$/, '/');
      console.log('Found loader script:', src);
      console.log('Extracted base URL:', baseUrl);
      return baseUrl;
    }
    
    // Fallback to any charm-lab script
    const scripts = document.querySelectorAll('script[src*="charm-lab"]');
    if (scripts.length > 0) {
      const src = scripts[0].src;
      const baseUrl = src.replace(/\/[^\/]*$/, '/');
      console.log('Found charm-lab script:', src);
      console.log('Extracted base URL:', baseUrl);
      return baseUrl;
    }
    
    // Final fallback
    console.log('Using fallback URL');
    return window.Shopify?.routes?.root + 'assets/' || '/assets/';
  }

  async loadAll() {
    if (this.isLoading) {
      return this.loadingPromise;
    }
    
    this.isLoading = true;
    console.log('🚀 Loading Charm Lab modules with NEW v2.1 Device-Specific Scaling...');
    
    try {
      // First, inject the NEW v2.1 device-specific scaling system directly
      this.injectScalingSystemV21();
      
      this.loadingPromise = this.executeLoading();
      await this.loadingPromise;
      
      console.log('✅ All Charm Lab modules loaded successfully');
      this.initializeCharmLab();
      
    } catch (error) {
      console.error('❌ Failed to load Charm Lab modules:', error);
      this.handleLoadingError(error);
    } finally {
      this.isLoading = false;
    }
    
    return this.loadingPromise;
  }

  /**
   * Inject the NEW v2.1 device-specific scaling system directly
   * NEW LOGIC: Mobile and Desktop use different scaling approaches
   */
  injectScalingSystemV21() {
    // Check if already loaded
    if (typeof window.FixedCanvasScalingSystem !== 'undefined') {
      console.log('📦 Fixed Canvas Scaling System already available - checking version...');
      try {
        const testInstance = new window.FixedCanvasScalingSystem();
        if (testInstance.version === '2.1.0') {
          console.log('✅ v2.1 device-specific scaling system already loaded');
          return;
        } else {
          console.log('⚠️ Old scaling system found, replacing with v2.1...');
        }
      } catch (e) {
        console.log('⚠️ Invalid scaling system found, replacing with v2.1...');
      }
    }

    // Create the NEW v2.1 device-specific scaling system class directly
    const scalingSystemCode = `
/**
 * Fixed Canvas Scaling System v2.1 - Injected by Loader
 * NEW LOGIC: Device-specific scaling behavior
 * Mobile: Base same size, charms scale by multiplier
 * Desktop: Base scales down, charms consistent
 */
class FixedCanvasScalingSystem {
  constructor() {
    this.baseImageUrl = null;
    this.baseTitle = null;
    this.baseScaleMultiplier = 1.0;
    this.baseScaleFactor = 1.0;
    this.charmScaleFactor = 1.0; // Device-specific scale factor for charms
    this.workspaceSize = this.getWorkspaceSize();
    this.canvasSize = 1000;
    this.isConfigured = false;
    this.version = '2.1.0';
    
    console.log('🎯 Fixed Canvas Scaling System v' + this.version + ' initialized - Workspace: ' + this.workspaceSize + 'px');
    console.log('📐 Device-Specific Logic: Mobile = base same size, charms scale by multiplier | Desktop = base scales down, charms consistent');
  }

  getWorkspaceSize() {
    if (window.innerWidth <= 768) {
      return Math.max(200, window.innerWidth - 160);
    } else {
      return 600;
    }
  }

  setBaseReference(imageUrl, title, scaleMultiplier = 1.0) {
    this.baseImageUrl = imageUrl;
    this.baseTitle = title;
    this.baseScaleMultiplier = parseFloat(scaleMultiplier) || 1.0;
    this.workspaceSize = this.getWorkspaceSize();
    
    // Calculate target size for multiplier 1.0 base jewelry
    let targetWorkspacePercent;
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      targetWorkspacePercent = 0.85;
    } else {
      targetWorkspacePercent = 0.75;
    }
    
    const targetNormalBaseSize = this.workspaceSize * targetWorkspacePercent;
    const normalBaseScaleFactor = targetNormalBaseSize / this.canvasSize;
    
    // DEVICE-SPECIFIC LOGIC
    if (isMobile) {
      // MOBILE: Base jewelry stays SAME size regardless of multiplier, charms scale BY multiplier
      this.baseScaleFactor = normalBaseScaleFactor; // Base always same size
      this.charmScaleFactor = normalBaseScaleFactor * this.baseScaleMultiplier; // Charms scale UP by multiplier
      
      console.log('📱 MOBILE scaling logic applied:');
      console.log('📐 Base scale: ' + this.baseScaleFactor.toFixed(3) + 'x (SAME size for all multipliers)');
      console.log('🔮 Charm scale: ' + this.charmScaleFactor.toFixed(3) + 'x (MULTIPLIED by ' + this.baseScaleMultiplier + 'x multiplier)');
      console.log('📊 Result: Base ' + Math.round(this.canvasSize * this.baseScaleFactor) + 'px, Charms scale ' + this.charmScaleFactor.toFixed(3) + 'x');
      
    } else {
      // DESKTOP: Base jewelry scales DOWN by multiplier, charms stay consistent
      this.baseScaleFactor = normalBaseScaleFactor / this.baseScaleMultiplier;
      this.charmScaleFactor = normalBaseScaleFactor;
      
      console.log('🖥️ DESKTOP scaling logic applied:');
      console.log('📐 Base scale: ' + this.baseScaleFactor.toFixed(3) + 'x (SCALES DOWN by ' + this.baseScaleMultiplier + 'x)');
      console.log('🔮 Charm scale: ' + this.charmScaleFactor.toFixed(3) + 'x (CONSISTENT for all base jewelry)');
      console.log('📊 Result: Base ' + Math.round(this.canvasSize * this.baseScaleFactor) + 'px, Charms scale ' + this.charmScaleFactor.toFixed(3) + 'x');
    }
    
    this.isConfigured = true;
    
    console.log('🎯 Fixed canvas base set: ' + title);
    console.log('📐 Scale multiplier: ' + this.baseScaleMultiplier + 'x');
    console.log('📏 Target size: ' + targetNormalBaseSize.toFixed(0) + 'px (' + (targetWorkspacePercent * 100).toFixed(0) + '% of ' + this.workspaceSize + 'px workspace)');
    
    return true;
  }

  calculateCharmSizeFromImage(charmImage) {
    if (!this.isConfigured) {
      console.warn('⚠️ Fixed canvas scaling not configured');
      return this.getFallbackCharmSize();
    }

    const imageWidth = charmImage.naturalWidth || charmImage.width;
    const imageHeight = charmImage.naturalHeight || charmImage.height;
    
    if (!imageWidth || !imageHeight) {
      console.warn('⚠️ Could not get image dimensions');
      return this.getFallbackCharmSize();
    }

    // Use device-specific charm scale factor
    const scaledWidth = imageWidth * this.charmScaleFactor;
    const scaledHeight = imageHeight * this.charmScaleFactor;
    
    const finalWidth = Math.round(scaledWidth);
    const finalHeight = Math.round(scaledHeight);
    
    const constraints = this.getCharmConstraints();
    const maxDimension = Math.max(finalWidth, finalHeight);
    const minDimension = Math.min(finalWidth, finalHeight);
    
    let constrainedWidth = finalWidth;
    let constrainedHeight = finalHeight;
    let constraintApplied = false;
    
    if (maxDimension > constraints.max) {
      const ratio = constraints.max / maxDimension;
      constrainedWidth = Math.round(finalWidth * ratio);
      constrainedHeight = Math.round(finalHeight * ratio);
      constraintApplied = true;
      console.warn('⚠️ Charm constrained for size: ' + finalWidth + 'x' + finalHeight + 'px → ' + constrainedWidth + 'x' + constrainedHeight + 'px (max: ' + constraints.max + 'px)');
    } else if (minDimension < constraints.min) {
      const ratio = constraints.min / minDimension;
      constrainedWidth = Math.round(finalWidth * ratio);
      constrainedHeight = Math.round(finalHeight * ratio);
      constraintApplied = true;
      console.warn('⚠️ Charm constrained for minimum size: ' + finalWidth + 'x' + finalHeight + 'px → ' + constrainedWidth + 'x' + constrainedHeight + 'px (min: ' + constraints.min + 'px)');
    }
    
    const isMobile = window.innerWidth <= 768;
    const scalingMethod = isMobile ? 'mobile-charms-scale-by-multiplier' : 'desktop-charms-consistent';
    
    // Enhanced logging for mobile debugging
    if (isMobile) {
      console.log('🔮 Mobile charm scaling: ' + imageWidth + 'x' + imageHeight + 'px × ' + this.charmScaleFactor.toFixed(3) + ' = ' + finalWidth + 'x' + finalHeight + 'px');
      if (constraintApplied) {
        console.log('  └─ Constrained to: ' + constrainedWidth + 'x' + constrainedHeight + 'px (workspace: ' + this.workspaceSize + 'px, max: ' + constraints.max + 'px)');
      } else {
        console.log('  └─ No constraints applied (within limits)');
      }
      console.log('  └─ Multiplier ' + this.baseScaleMultiplier + 'x: charms ' + (this.baseScaleMultiplier > 1 ? 'enlarged' : 'normal'));
    } else {
      console.log('🔮 Desktop charm scaling: ' + imageWidth + 'x' + imageHeight + 'px × ' + this.charmScaleFactor.toFixed(3) + ' = ' + constrainedWidth + 'x' + constrainedHeight + 'px');
      console.log('  └─ Desktop: Charm scale INDEPENDENT of base multiplier (' + this.baseScaleMultiplier + 'x)');
    }
    
    return {
      width: constrainedWidth,
      height: constrainedHeight,
      scaleFactor: this.charmScaleFactor,
      originalWidth: imageWidth,
      originalHeight: imageHeight,
      scaledWidth: finalWidth,
      scaledHeight: finalHeight,
      constraintApplied: constraintApplied,
      baseScaleFactor: this.baseScaleFactor,
      baseScaleMultiplier: this.baseScaleMultiplier,
      charmScaleFactor: this.charmScaleFactor,
      method: scalingMethod,
      deviceType: isMobile ? 'mobile' : 'desktop'
    };
  }

  getBaseSize() {
    if (!this.isConfigured) {
      return this.getFallbackBaseSize();
    }

    // Use device-specific base scale factor
    const scaledSize = Math.round(this.canvasSize * this.baseScaleFactor);
    
    const maxSize = Math.floor(this.workspaceSize * 0.9);
    const minSize = Math.floor(this.workspaceSize * 0.1);
    const finalSize = Math.max(minSize, Math.min(maxSize, scaledSize));
    
    const isMobile = window.innerWidth <= 768;
    
    console.log('📏 ' + (isMobile ? 'Mobile' : 'Desktop') + ' base size: ' + this.canvasSize + 'px × ' + this.baseScaleFactor.toFixed(3) + ' = ' + scaledSize + 'px → ' + finalSize + 'px');
    if (isMobile) {
      console.log('  └─ Mobile: Base multiplier ' + this.baseScaleMultiplier + 'x does NOT affect size (stays ' + finalSize + 'px)');
    } else {
      console.log('  └─ Desktop: Base multiplier ' + this.baseScaleMultiplier + 'x scales DOWN to ' + finalSize + 'px');
    }
    
    return {
      width: finalSize,
      height: finalSize,
      scaleFactor: this.baseScaleFactor,
      baseScaleFactor: this.baseScaleFactor,
      scaleMultiplier: this.baseScaleMultiplier,
      charmScaleFactor: this.charmScaleFactor,
      workspacePercent: ((finalSize / this.workspaceSize) * 100).toFixed(1),
      isConfigured: true,
      method: isMobile ? 'mobile-base-same-size' : 'desktop-base-scales-down',
      deviceType: isMobile ? 'mobile' : 'desktop'
    };
  }

  getCharmConstraints() {
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // Mobile: More generous constraints to allow proper scaling with multipliers
      return {
        min: 10, // Lower minimum
        max: Math.floor(this.workspaceSize * 0.8) // Increased from 0.5 to 0.8 (80% of workspace)
      };
    } else {
      // Desktop: Keep existing constraints
      return {
        min: 10,
        max: Math.floor(this.workspaceSize * 0.4)
      };
    }
  }

  getFallbackCharmSize() {
    const size = window.innerWidth <= 768 ? 60 : 80;
    return {
      width: size,
      height: size,
      scaleFactor: 1,
      isConfigured: false,
      method: 'fallback'
    };
  }

  getFallbackBaseSize() {
    const size = window.innerWidth <= 768 ? 150 : 200;
    return {
      width: size,
      height: size,
      scaleFactor: 1,
      isConfigured: false
    };
  }

  handleResize() {
    const oldWorkspaceSize = this.workspaceSize;
    this.workspaceSize = this.getWorkspaceSize();
    
    if (Math.abs(oldWorkspaceSize - this.workspaceSize) > 20) {
      console.log('📱 Workspace resized: ' + oldWorkspaceSize + 'px → ' + this.workspaceSize + 'px');
      
      if (this.isConfigured) {
        // Recalculate with device-specific logic
        const isMobile = window.innerWidth <= 768;
        const targetWorkspacePercent = isMobile ? 0.85 : 0.75;
        const targetNormalBaseSize = this.workspaceSize * targetWorkspacePercent;
        const normalBaseScaleFactor = targetNormalBaseSize / this.canvasSize;
        
        if (isMobile) {
          // Mobile: base same size, charms scale UP by multiplier
          this.baseScaleFactor = normalBaseScaleFactor;
          this.charmScaleFactor = normalBaseScaleFactor * this.baseScaleMultiplier;
        } else {
          // Desktop: base scales down, charms consistent
          this.baseScaleFactor = normalBaseScaleFactor / this.baseScaleMultiplier;
          this.charmScaleFactor = normalBaseScaleFactor;
        }
        
        console.log('🔄 Recalculated ' + (isMobile ? 'mobile' : 'desktop') + ' scaling:');
        console.log('  Base: ' + this.baseScaleFactor.toFixed(3) + 'x');
        console.log('  Charms: ' + this.charmScaleFactor.toFixed(3) + 'x');
      }
      
      return true;
    }
    
    return false;
  }

  getScalingStats() {
    const isMobile = window.innerWidth <= 768;
    
    return {
      version: this.version,
      isConfigured: this.isConfigured,
      canvasSize: this.canvasSize,
      workspaceSize: this.workspaceSize,
      baseScaleFactor: this.baseScaleFactor,
      baseScaleMultiplier: this.baseScaleMultiplier,
      charmScaleFactor: this.charmScaleFactor,
      baseImageUrl: this.baseImageUrl,
      baseTitle: this.baseTitle,
      device: isMobile ? 'mobile' : 'desktop',
      scalingMethod: isMobile ? 'mobile-base-same-charms-scale' : 'desktop-base-scales-charms-consistent',
      baseDisplaySize: this.isConfigured ? Math.round(this.canvasSize * this.baseScaleFactor) : 'N/A',
      normalBaseSize: this.isConfigured ? Math.round(this.canvasSize * (isMobile ? this.baseScaleFactor : this.charmScaleFactor)) : 'N/A',
      logic: isMobile ? 'Mobile: Base same size, charms scale by multiplier' : 'Desktop: Base scales down, charms consistent'
    };
  }

  debugScaling() {
    console.group('🔍 Fixed Canvas Scaling Debug v2.1 - Device-Specific Logic');
    console.table(this.getScalingStats());
    
    if (this.isConfigured) {
      const isMobile = window.innerWidth <= 768;
      const baseSize = this.getBaseSize();
      console.log('Base jewelry display size:', baseSize);
      
      if (isMobile) {
        console.log('\\n📱 MOBILE SCALING LOGIC:');
        console.log('--- Base jewelry: SAME size regardless of multiplier');
        console.log('--- Charms: Scale BY multiplier (2.0x base = larger charms)');
        console.log('--- Base with any multiplier: ' + Math.round(this.canvasSize * this.baseScaleFactor) + 'px');
        console.log('--- Charms scale at: ' + this.charmScaleFactor.toFixed(3) + 'x (multiplied by base multiplier)');
      } else {
        console.log('\\n🖥️ DESKTOP SCALING LOGIC:');
        console.log('--- Base jewelry: Scales DOWN by multiplier');
        console.log('--- Charms: CONSISTENT size regardless of base multiplier');
        console.log('--- Base 1.0x: ' + Math.round(this.canvasSize * this.charmScaleFactor) + 'px');
        console.log('--- Base 2.0x: ' + Math.round(this.canvasSize * this.charmScaleFactor / 2.0) + 'px');
        console.log('--- Charms scale at: ' + this.charmScaleFactor.toFixed(3) + 'x (consistent)');
      }
    } else {
      console.log('❌ System not configured');
    }
    
    console.groupEnd();
  }

  testScalingLogic() {
    console.group('🧪 Testing v2.1 Device-Specific Logic');
    
    if (!this.isConfigured) {
      console.log('❌ System not configured for testing');
      console.groupEnd();
      return;
    }
    
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      console.log('📱 Mobile Logic Test:');
      console.log('Base size: ' + Math.round(this.canvasSize * this.baseScaleFactor) + 'px (same for all multipliers)');
      
      const charm1xScale = this.baseScaleFactor; // For 1.0x multiplier
      const charm2xScale = this.baseScaleFactor * 2.0; // For 2.0x multiplier
      console.log('Charm on 1.0x base: ' + charm1xScale.toFixed(3) + 'x scale');
      console.log('Charm on 2.0x base: ' + charm2xScale.toFixed(3) + 'x scale (larger)');
      console.log('✅ Result: Charms scale proportionally LARGER with base jewelry multiplier');
      
    } else {
      console.log('🖥️ Desktop Logic Test:');
      console.log('Base 1.0x: ' + Math.round(this.canvasSize * this.charmScaleFactor) + 'px');
      console.log('Base 2.0x: ' + Math.round(this.canvasSize * this.charmScaleFactor / 2.0) + 'px (half size)');
      console.log('Charms: ' + this.charmScaleFactor.toFixed(3) + 'x scale (consistent)');
      console.log('✅ Result: Charms appear same size regardless of base jewelry');
    }
    
    console.groupEnd();
  }

  reset() {
    this.baseImageUrl = null;
    this.baseTitle = null;
    this.baseScaleMultiplier = 1.0;
    this.baseScaleFactor = 1.0;
    this.charmScaleFactor = 1.0;
    this.isConfigured = false;
    console.log('🔄 Fixed canvas scaling system v2.1 reset');
  }
}

// Make available globally
window.FixedCanvasScalingSystem = FixedCanvasScalingSystem;
`;

    // Execute the code
    try {
      const script = document.createElement('script');
      script.textContent = scalingSystemCode;
      document.head.appendChild(script);
      
      console.log('📦 Fixed Canvas Scaling System v2.1 injected successfully');
      console.log('🎯 NEW LOGIC: Device-specific scaling behavior');
      console.log('📱 Mobile: Base same size, charms scale by multiplier');
      console.log('🖥️ Desktop: Base scales down, charms consistent');
      this.loadedModules.add('charm-lab-scaling-system.js');
      
      // Test the new system
      try {
        const testSystem = new FixedCanvasScalingSystem();
        console.log('✅ v2.1 Device-specific scaling system test passed');
        testSystem.debugScaling();
        
        if (testSystem.testScalingLogic) {
          testSystem.testScalingLogic();
        }
      } catch (error) {
        console.error('❌ v2.1 Scaling system test failed:', error);
      }
      
    } catch (error) {
      console.error('❌ Failed to inject v2.1 scaling system:', error);
    }
  }

  async executeLoading() {
    // Skip the scaling system since we injected it
    const modulesToLoad = this.modules.filter(m => m !== 'charm-lab-scaling-system.js');
    
    for (const moduleName of modulesToLoad) {
      await this.loadModule(moduleName);
    }
  }

  async loadModule(moduleName) {
    if (this.loadedModules.has(moduleName)) {
      return;
    }
    
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = this.baseUrl + moduleName;
      script.async = false;
      script.defer = true;
      
      script.onload = () => {
        this.loadedModules.add(moduleName);
        console.log('📦 Loaded: ' + moduleName);
        resolve();
      };
      
      script.onerror = () => {
        const error = new Error('Failed to load ' + moduleName + ' from ' + script.src);
        console.error('❌ Error loading ' + moduleName + ':', error);
        reject(error);
      };
      
      document.head.appendChild(script);
    });
  }

  initializeCharmLab() {
    if (typeof CharmLab !== 'undefined') {
      console.log('🎯 Charm Lab ready with NEW v2.1 Device-Specific Scaling!');
      
      const systemStatus = {
        fixedCanvasScaling: typeof FixedCanvasScalingSystem !== 'undefined',
        hasInteractJs: typeof interact !== 'undefined',
        scalingVersion: '2.1.0'
      };
      
      if (systemStatus.fixedCanvasScaling) {
        console.log('🎯 Fixed Canvas Scaling System v2.1 loaded successfully');
        console.log('📐 Device-Specific Logic: Mobile and Desktop use different scaling approaches');
        
        // Test the scaling system
        try {
          const testSystem = new FixedCanvasScalingSystem();
          console.log('✅ v2.1 Device-specific scaling system test passed');
          
          // Run logic test
          if (testSystem.testScalingLogic) {
            testSystem.testScalingLogic();
          }
        } catch (error) {
          console.error('❌ v2.1 Scaling system test failed:', error);
        }
      } else {
        console.warn('⚠️ Fixed Canvas Scaling System v2.1 not available');
      }
      
      if (systemStatus.hasInteractJs) {
        console.log('🖱️ Interact.js loaded - drag & drop ready');
      } else {
        console.warn('⚠️ Interact.js not found - charm interactions may not work');
      }
      
      const event = new CustomEvent('charmLabReady', {
        detail: { 
          loader: this, 
          timestamp: Date.now(),
          modulesLoaded: Array.from(this.loadedModules),
          scalingFeatures: {
            fixedCanvasScaling: systemStatus.fixedCanvasScaling,
            deviceSpecificLogic: true,
            mobileBaseSameSize: true,
            mobileCharmsScaleByMultiplier: true,
            desktopBaseScalesDown: true,
            desktopCharmsConsistent: true,
            optimizedForWorkspace: true,
            scaleMultiplierSupport: true,
            imageDimensionBased: true,
            version: '2.1.0'
          },
          canvasInfo: {
            baseImageSize: '1000x1000px fixed canvas',
            workspaceUtilization: '75% desktop, 85% mobile',
            scalingMethod: 'Device-Specific: Mobile=base same/charms scale, Desktop=base scales/charms consistent',
            metafieldsRequired: 'charm_builder_image_scale only',
            imageBasedScaling: true,
            mobileLogic: 'Base jewelry same size, charms scale by multiplier',
            desktopLogic: 'Base jewelry scales down, charms consistent size',
            examples: [
              'Mobile: Base 1.0x→200px, Base 2.0x→200px (same)',
              'Mobile: Charms 1.0x→0.2x, Charms 2.0x→0.4x (double)',
              'Desktop: Base 1.0x→450px, Base 2.0x→225px (half)',
              'Desktop: Charms→0.45x on both (consistent)'
            ]
          }
        }
      });
      document.dispatchEvent(event);
      
    } else {
      console.warn('⚠️ CharmLab class not found after loading modules');
    }
  }

  handleLoadingError(error) {
    console.error('Charm Lab loading failed:', {
      error: error.message,
      loadedModules: Array.from(this.loadedModules),
      remainingModules: this.modules.filter(m => !this.loadedModules.has(m)),
      baseUrl: this.baseUrl
    });
    
    const errorEvent = new CustomEvent('charmLabLoadError', {
      detail: { error, loader: this }
    });
    document.dispatchEvent(errorEvent);
    
    this.showErrorMessage();
  }

  showErrorMessage() {
    const containers = document.querySelectorAll('[id^="charm-lab-"]');
    
    containers.forEach(container => {
      container.innerHTML = `
        <div style="text-align: center; padding: 2rem; color: #666;">
          <h3>Charm Builder Loading Error</h3>
          <p>We're having trouble loading the charm builder. Please refresh the page or try again later.</p>
          <button onclick="window.location.reload()" style="
            background: #000; 
            color: white; 
            border: none; 
            padding: 0.75rem 1.5rem; 
            border-radius: 4px; 
            cursor: pointer;
            margin-top: 1rem;
          ">
            Refresh Page
          </button>
        </div>
      `;
    });
  }

  isFullyLoaded() {
    return this.loadedModules.size === this.modules.length;
  }

  getLoadingProgress() {
    return Math.round((this.loadedModules.size / this.modules.length) * 100);
  }

  getSystemInfo() {
    return {
      scalingMethod: 'Fixed Canvas (1000x1000px) - v2.1 Device-Specific Logic',
      mobileLogic: 'Base jewelry same size, charms scale by multiplier',
      desktopLogic: 'Base jewelry scales down by multiplier, charms stay consistent',
      workspaceOptimization: 'Uses 75% of desktop workspace, 85% of mobile',
      metafieldsRequired: 'charm_builder_image_scale only',
      imageBasedScaling: true,
      charmImagesDimensions: 'Native dimensions × device-specific charm scale factor',
      scalingCalculation: {
        mobile: 'Base: normal_scale (same) | Charms: normal_scale ÷ multiplier',
        desktop: 'Base: normal_scale ÷ multiplier | Charms: normal_scale (consistent)'
      },
      advantages: [
        'Device-appropriate scaling behavior',
        'Mobile: Natural proportional charm scaling',
        'Desktop: Consistent charm appearance across jewelry types',
        'One scaling system handles both use cases',
        'Better user experience on each device type'
      ],
      examples: [
        'Mobile - Base 1.0x: 200px, charms 0.2x | Base 2.0x: 200px, charms 0.4x',
        'Desktop - Base 1.0x: 450px, charms 0.45x | Base 2.0x: 225px, charms 0.45x'
      ]
    };
  }
}

// Create global loader instance
const charmLabLoader = new CharmLabLoader();

// Auto-start loading
function startCharmLabLoader() {
  if (typeof interact === 'undefined') {
    console.warn('⚠️ Interact.js not found. Charm drag/rotate functionality may not work.');
  }
  
  charmLabLoader.loadAll();
}

// Multiple DOM ready detection methods
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startCharmLabLoader);
} else if (document.readyState === 'interactive') {
  if (document.querySelector('[id^="charm-lab-"]')) {
    startCharmLabLoader();
  } else {
    setTimeout(startCharmLabLoader, 100);
  }
} else {
  startCharmLabLoader();
}

// Fallback
setTimeout(() => {
  if (!charmLabLoader.isLoading && !charmLabLoader.isFullyLoaded()) {
    console.log('🔄 Fallback: Starting Charm Lab loader...');
    startCharmLabLoader();
  }
}, 1000);

// Event listeners
document.addEventListener('charmLabReady', (event) => {
  const { scalingFeatures, canvasInfo } = event.detail;
  console.log('🎉 Charm Lab initialized with Fixed Canvas Scaling v2.1');
  console.log('🎯 Canvas Info:', canvasInfo);
  console.log('📐 Device-Specific Logic:');
  console.log('📱 Mobile: Base jewelry same size, charms scale by multiplier');
  console.log('🖥️ Desktop: Base jewelry scales down, charms consistent');
  console.log('📊 Examples:');
  canvasInfo.examples.forEach(example => console.log('  ' + example));
  console.log('✅ Result: Optimal scaling behavior for each device type');
});

document.addEventListener('charmLabLoadError', (event) => {
  console.error('🚨 Charm Lab failed to load:', event.detail.error);
});

// Global debug helper
window.debugCharmLab = function() {
  console.group('🔍 Charm Lab Debug Info v2.1');
  console.log('Loader:', charmLabLoader);
  console.log('System Info:', charmLabLoader.getSystemInfo());
  console.log('Loading Progress:', charmLabLoader.getLoadingProgress() + '%');
  console.log('Fixed Canvas Scaling v2.1 Available:', typeof FixedCanvasScalingSystem !== 'undefined');
  console.log('Interact.js Available:', typeof interact !== 'undefined');
  
  if (typeof window.CharmLab !== 'undefined') {
    const containers = document.querySelectorAll('[id^="charm-lab-"]');
    console.log('Charm Lab Instances:', containers.length);
    
    containers.forEach(container => {
      if (container.__charmLab) {
        console.log('Instance:', container.id, container.__charmLab);
        
        // Test the NEW v2.1 device-specific scaling system
        if (container.__charmLab.scalingSystem) {
          console.log('🎯 Testing v2.1 device-specific scaling system...');
          container.__charmLab.scalingSystem.debugScaling();
          
          if (container.__charmLab.scalingSystem.testScalingLogic) {
            container.__charmLab.scalingSystem.testScalingLogic();
          }
        }
      }
    });
  }
  
  console.groupEnd();
};

// Export for external access
window.charmLabLoader = charmLabLoader;